import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FRAMEWORKS } from "@/data/testimonials";
import { trackEvent } from "@/lib/api";
import {
    ArrowRight, CalendarClock, CheckCircle2, Database, Fingerprint, FlaskConical, Globe, LockKeyhole, MessageSquare,
    ShieldCheck, Sparkles, UserCheck, Workflow, Zap,
} from "lucide-react";

const ENTERPRISE_CAPABILITIES = [
    {
        icon: Database,
        title: "Automated Evidence Ingestion",
        body: "Live read-only connectors for Okta, AWS and GitHub. How it works: 1 — connect with least-privilege read-only credentials, 2 — AuditVanguard ingests MFA coverage, stale credentials, branch-protection gaps, dependency alerts and log coverage, 3 — each finding lands on the right control automatically.",
        chip: "Connectors · live",
        cta: { label: "Integration docs & required permissions →", href: "/docs/integrations" },
    },
    {
        icon: Globe,
        title: "Global Framework Support",
        body: "Full cross-mapping for SOC 2, ISO 27001, ISO 42001 (AIMS), PCI DSS, HIPAA, CMMC 2.0 (Level 1 & 2), GDPR, NIST AI RMF and the new EU AI Act (Articles 9, 10, 13, 14, 15, 62). One control, every framework.",
        chip: `${FRAMEWORKS.length} frameworks`,
    },
    {
        icon: Fingerprint,
        title: "Automated Audit Trails",
        body: "Every action — file uploads, note edits, task status, control state changes, PDF exports, auditor comments — is written to a SHA-256 hash-chained log that timestamps itself. Non-editable, tamper-evident, and exportable as a signed JSON any third party can verify.",
        chip: "Tamper-evident",
        cta: { label: "Verify a signed log →", href: "/verify-audit-log" },
    },
];

const AI_EXCELLENCE = [
    {
        icon: Zap,
        title: "Magic Mapping",
        body: "Drop an evidence file — AuditVanguard reads it and suggests the exact controls it satisfies across every framework. No more tagging matrices by hand.",
        chip: "Evidence",
    },
    {
        icon: Sparkles,
        title: "AI Compliance Assistant",
        body: "Ask a plain-English question about any control or auditor flag and get an expert-grade answer, cited to the client's own evidence and notes. Right where you're working.",
        chip: "In-context Q&A",
    },
    {
        icon: Workflow,
        title: "Smart Readiness Insights",
        body: "AI runs a live gap analysis on every workspace — the top three risks, the fastest wins, and the exact controls holding back an audit sign‑off.",
        chip: "Gap analysis",
    },
];

const LIFECYCLE = [
    {
        icon: UserCheck,
        title: "Auditor Portal",
        body: "Invite official auditors into a dedicated read-only workspace. They review the same evidence you and the client see — without email attachments or file exports.",
        chip: "Read-only access",
    },
    {
        icon: MessageSquare,
        title: "Flag & Reply Threads",
        body: "Auditors flag any control or file with a priority. You and the client reply in-thread with screenshots and links. Bulk-resolve when the queue is done.",
        chip: "Zero email chains",
    },
    {
        icon: LockKeyhole,
        title: "Secure Document Vault",
        body: "NDA-gated access with versioned evidence history. Every download is logged.",
        chip: "NDA-gated",
    },
];

export const EnterpriseCapabilitiesSection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
        data-testid="enterprise-capabilities-section"
    >
        <div className="max-w-3xl mb-14">
            <div className="mono-label mb-3 inline-flex items-center gap-2">
                <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />
                Enterprise capabilities
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                Built for the
                <br />
                AI compliance era.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-xl">
                Automated audit trails, cross-framework coverage, AI-native
                evidence review, and a real Auditor Portal — the pillars compliance
                firms and their auditors have been asking for.
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ENTERPRISE_CAPABILITIES.map((f) => (
                <div
                    key={f.title}
                    className="group border border-border rounded-2xl bg-card p-8 hover:-translate-y-1 transition-transform duration-300"
                    data-testid={`enterprise-capability-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <div className="w-12 h-12 rounded-xl grid place-items-center bg-slate-900 text-white mb-4">
                        <f.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div className="mb-6">
                        <Badge
                            variant="outline"
                            className="rounded-full border-border/70 text-xs font-mono tracking-widest uppercase whitespace-nowrap"
                        >
                            {f.chip}
                        </Badge>
                    </div>
                    <div className="font-display text-2xl font-semibold tracking-tight">
                        {f.title}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                        {f.body}
                    </p>
                    {f.cta && (
                        <Link
                            to={f.cta.href}
                            className="text-sm underline underline-offset-4 mt-4 inline-flex items-center text-muted-foreground hover:text-foreground transition-colors tap-target"
                            data-testid={`enterprise-capability-cta-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {f.cta.label}
                        </Link>
                    )}
                </div>
            ))}
        </div>

        <FeatureStatusTable />
    </section>
);

const STATUS_STYLE = {
    Live: { icon: CheckCircle2, cls: "status-live" },
    Beta: { icon: FlaskConical, cls: "status-beta" },
    Roadmap: { icon: CalendarClock, cls: "text-muted-foreground" },
};

const FEATURE_STATUS = [
    ["Okta connector (read-only)", "Live"],
    ["AWS connector (read-only)", "Live"],
    ["GitHub connector (read-only)", "Live"],
    ["Auditor Portal", "Live"],
    ["Branded PDF exports", "Live"],
    ["EU AI Act control mapping", "Live"],
    ["Microsoft Entra ID connector", "Roadmap"],
    ["Google Workspace connector", "Roadmap"],
    ["Jira connector", "Roadmap"],
    ["Nightly connector auto-sync", "Live"],
];

const FeatureStatusTable = () => (
    <div className="mt-12 rounded-2xl border border-border overflow-hidden" data-testid="feature-status-table">
        <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
                <caption className="sr-only">Feature availability: Live, Beta or Roadmap</caption>
                <thead>
                    <tr className="border-b border-border bg-card">
                        <th scope="col" className="text-left px-5 py-3 mono-label">Feature</th>
                        <th scope="col" className="text-left px-5 py-3 mono-label">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {FEATURE_STATUS.map(([feature, status]) => {
                        const S = STATUS_STYLE[status];
                        return (
                            <tr key={feature}>
                                <td className="px-5 py-3">{feature}</td>
                                <td className="px-5 py-3">
                                    <span className={`inline-flex items-center gap-1.5 font-medium ${S.cls}`}>
                                        <S.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                                        {status}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

export const PricingSection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
        data-testid="pricing-section-landing"
    >
        <div className="max-w-3xl">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                Pricing.
            </h2>
            <div className="font-display text-xl sm:text-2xl font-semibold text-muted-foreground mt-4" data-testid="enterprise-price-line">
                $199/mo · unlimited clients
            </div>
            <p className="mt-4 text-muted-foreground max-w-xl">
                One firm-wide price: <span className="font-semibold text-foreground">$199/month, unlimited clients</span>.
            </p>
        </div>
    </section>
);

export const AiExcellenceSection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
        data-testid="ai-excellence-section"
    >
        <div className="max-w-3xl mb-14">
            <div className="mono-label mb-3 inline-flex items-center gap-2">
                <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                AI excellence
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                Every workflow,
                <br />
                answered by AI.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-xl">
                AuditVanguard&apos;s assistant lives inside every control, every file, and
                every auditor flag — grounded in your client&apos;s own evidence, not the
                open internet. Client data is not used to train models.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="ai-excellence-grid">
            <div
                className="lg:col-span-5 border border-border rounded-2xl bg-card p-8 flex flex-col"
                data-testid={`ai-excellence-${AI_EXCELLENCE[0].title.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {React.createElement(AI_EXCELLENCE[0].icon, { className: "h-6 w-6", strokeWidth: 1.5 })}
                <Badge
                    variant="outline"
                    className="rounded-md border-border/70 text-xs font-mono tracking-widest uppercase w-fit mt-8"
                >
                    {AI_EXCELLENCE[0].chip}
                </Badge>
                <div className="font-display text-2xl sm:text-3xl font-semibold mt-4 tracking-tight">
                    {AI_EXCELLENCE[0].title}
                </div>
                <p className="text-base text-muted-foreground leading-relaxed mt-3">
                    {AI_EXCELLENCE[0].body}
                </p>
            </div>
            <div className="lg:col-span-7 border-t border-border">
                {AI_EXCELLENCE.slice(1).map((f) => (
                    <div
                        key={f.title}
                        className="flex items-start gap-6 py-6 border-b border-border"
                        data-testid={`ai-excellence-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <f.icon className="h-5 w-5 mt-1 shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="font-display text-lg font-semibold">{f.title}</span>
                                <span className="text-xs font-mono tracking-widest uppercase text-muted-foreground">
                                    {f.chip}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">
                                {f.body}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

export const AimsSection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
        data-testid="aims-section"
    >
        <div className="max-w-3xl mb-14">
            <div className="mono-label mb-3 inline-flex items-center gap-2">
                <UserCheck className="h-3 w-3" strokeWidth={1.5} />
                AIMS &amp; auditor collaboration
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                The whole compliance audit lifecycle.
                <br />
                Under one roof.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-xl">
                From client onboarding to auditor sign‑off — consultants, clients
                and official auditors work in the same workspace with the same
                source of truth. No email chains. No lost evidence.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="aims-grid">
            <div className="lg:col-span-7 border-t border-border lg:order-1 order-2">
                {LIFECYCLE.slice(1).map((f) => (
                    <div
                        key={f.title}
                        className="flex items-start gap-6 py-6 border-b border-border"
                        data-testid={`aims-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <f.icon className="h-5 w-5 mt-1 shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="font-display text-lg font-semibold">{f.title}</span>
                                <span className="text-xs font-mono tracking-widest uppercase text-muted-foreground">
                                    {f.chip}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">
                                {f.body}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            <div
                className="lg:col-span-5 border border-border rounded-2xl bg-card p-8 flex flex-col lg:order-2 order-1"
                data-testid={`aims-${LIFECYCLE[0].title.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {React.createElement(LIFECYCLE[0].icon, { className: "h-6 w-6", strokeWidth: 1.5 })}
                <Badge
                    variant="outline"
                    className="rounded-md border-border/70 text-xs font-mono tracking-widest uppercase w-fit mt-8"
                >
                    {LIFECYCLE[0].chip}
                </Badge>
                <div className="font-display text-2xl sm:text-3xl font-semibold mt-4 tracking-tight">
                    {LIFECYCLE[0].title}
                </div>
                <p className="text-base text-muted-foreground leading-relaxed mt-3">
                    {LIFECYCLE[0].body}
                </p>
            </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-slate-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="aims-cta">
            <div className="flex-1">
                <div className="font-display text-2xl font-semibold">
                    Ready for auditor sign‑off day one.
                </div>
                <div className="text-sm text-slate-300 mt-1.5 max-w-xl">
                    Every workspace ships with versioned evidence history and a
                    branded PDF export your auditor actually wants to read.
                </div>
            </div>
            <Link to="/register" className="shrink-0 w-full sm:w-auto">
                <Button
                    size="lg"
                    className="rounded-md font-display bg-white text-slate-900 hover:bg-slate-100 px-6 h-[52px] w-full sm:w-auto"
                    data-testid="aims-cta-btn"
                    onClick={() =>
                        trackEvent("landing_cta_click", {
                            location: "aims",
                            label: "start_audit",
                        })
                    }
                >
                    Start free
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
            </Link>
        </div>
    </section>
);
